import React, { useState } from 'react';
import { ChevronDown, MessageCircle, Phone, Mail } from 'lucide-react';
import { faqData } from '../mock';

const FAQ = () => {
  const [openItem, setOpenItem] = useState(null);
  const [showWarrantyTerms, setShowWarrantyTerms] = useState(false);

  const toggleItem = (id) => {
    setOpenItem(openItem === id ? null : id);
  };

  return (
    <section className="pad-120 bg-gradient-to-b from-black via-gray-950 to-black">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block mb-4 px-4 py-2 bg-blue-500/10 rounded-full">
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-wider">FAQ</span>
          </div>
          <h2 className="heading-1 mb-6 bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
            Frequently Asked Questions
          </h2>
          <p className="body-large text-gray-400 max-w-3xl mx-auto">
            Everything you need to know about our premium mobile detailing and ceramic coating services
          </p>
        </div>

        {/* FAQ Grid */}
        <div className="max-w-5xl mx-auto">
          <div className="space-y-4">
            {faqData.map((faq) => (
              <div 
                key={faq.id}
                className="group bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden hover:border-blue-500/30 transition-all duration-300"
              >
                <button
                  onClick={() => toggleItem(faq.id)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-800/30 transition-colors duration-200"
                >
                  <span className="heading-3 text-base text-white pr-4 group-hover:text-blue-400 transition-colors">
                    {faq.question}
                  </span>
                  <ChevronDown 
                    className={`flex-shrink-0 text-blue-400 transition-transform duration-300 ${
                      openItem === faq.id ? 'rotate-180' : ''
                    }`}
                    size={24}
                  />
                </button>
                
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openItem === faq.id ? 'max-h-[2000px]' : 'max-h-0'
                  }`}
                >
                  <div className="px-4 md:px-8 pb-6 pt-2 border-t border-gray-800/50">
                    <div className="body-medium text-gray-300 whitespace-pre-line leading-relaxed break-words">
                      {faq.answer}
                      {faq.id === 9 && (
                        <div className="mt-4">
                          <button
                            onClick={() => setShowWarrantyTerms(!showWarrantyTerms)}
                            className="text-blue-400 hover:text-blue-300 text-sm font-semibold flex items-center gap-2"
                          >
                            <ChevronDown 
                              className={`transition-transform duration-300 ${
                                showWarrantyTerms ? 'rotate-180' : ''
                              }`}
                              size={16}
                            />
                            Warranty Terms & Conditions
                          </button>
                          
                          {showWarrantyTerms && (
                            <div className="mt-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                              <p className="text-xs text-gray-400 leading-relaxed mb-3">
                                <strong className="text-gray-300">Warranty Coverage Requirements:</strong>
                              </p>
                              <ul className="text-xs text-gray-400 space-y-2 list-disc list-inside">
                                <li>Annual maintenance visit required to maintain warranty coverage</li>
                                <li>Ceramic coating topper application must be performed once per year</li>
                                <li>Vehicle must be properly maintained (regular washing, no harsh chemicals)</li>
                                <li>Warranty covers material defects and application failures only</li>
                                <li>Does not cover damage from accidents, improper care, or environmental factors</li>
                                <li>Proof of annual maintenance service must be retained</li>
                                <li>Warranty is non-transferable and applies to original vehicle owner only</li>
                              </ul>
                              <p className="text-xs text-gray-500 mt-3 italic">
                                For complete warranty terms, please contact us directly.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA Section */}
        <div className="mt-20 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-blue-500/10 via-transparent to-blue-500/10 rounded-3xl p-8 border border-blue-500/20">
            <div className="text-center mb-8">
              <h3 className="heading-2 text-3xl mb-4">Still Have Questions?</h3>
              <p className="body-medium text-gray-400">
                Our team is ready to assist you with any inquiries about our detailing and ceramic coating services
              </p>
            </div>
            
            {/* Contact Options */}
            <div className="grid md:grid-cols-3 gap-4">
              <a
                href="tel:6046138901"
                className="flex items-center justify-center gap-3 px-6 py-4 bg-gray-900/50 hover:bg-gray-800 border border-gray-800 hover:border-blue-500/50 rounded-xl transition-all duration-300 group"
              >
                <Phone size={20} className="text-blue-400 group-hover:scale-110 transition-transform" />
                <div className="text-left">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Call Us</div>
                  <div className="text-white font-semibold">(604) 613-8901</div>
                </div>
              </a>
              
              <a
                href="mailto:info@prowashmd.com"
                className="flex items-center justify-center gap-3 px-6 py-4 bg-gray-900/50 hover:bg-gray-800 border border-gray-800 hover:border-blue-500/50 rounded-xl transition-all duration-300 group"
              >
                <Mail size={20} className="text-blue-400 group-hover:scale-110 transition-transform" />
                <div className="text-left">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Email</div>
                  <div className="text-white font-semibold">Get in Touch</div>
                </div>
              </a>
              
              <button
                onClick={() => {
                  const element = document.querySelector('#booking-walkthrough');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex items-center justify-center gap-3 px-6 py-4 bg-blue-500 hover:bg-blue-600 border border-blue-500 rounded-xl transition-all duration-300 group"
              >
                <MessageCircle size={20} className="text-white group-hover:scale-110 transition-transform" />
                <div className="text-left">
                  <div className="text-xs text-blue-100 uppercase tracking-wide">Book Now</div>
                  <div className="text-white font-semibold">Schedule Service</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;